import { useEffect } from "react";
import { NavLink, Outlet } from "react-router";

import { useGlobalContext } from "app/store/GlobalContext";
import { LinkPrimaryArrow } from "components/link/Link";
import { SideMenu } from "components/menu/SideMenu/SideMenu";
import { captureEvent } from "components/posthog/customEventCapture";
import InfoIconTooltip from "components/tooltip/InfoIconTooltip";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { usePermission } from "hooks/use-permissions";
import { useRootStore } from "store";

export default function DocumentDelivery() {
  const customerId = useRequiredCustomerId();
  const { archive } = useRootStore((state) => state);
  const { fetchUploadedFiles } = useRootStore((state) => state.delivery);
  const { handleNavigation } = useGlobalContext();
  const isInternalUser = usePermission("isInternal");

  const { fetchArchiveFolders } = archive;

  useEffect(() => {
    fetchArchiveFolders(customerId);
    fetchUploadedFiles(customerId);
  }, [customerId, fetchUploadedFiles, fetchArchiveFolders]);

  return (
    <div
      data-testid="document-delivery"
      className="flex-column-md-down d-flex flex-wrap align-items-start"
    >
      <SideMenu
        title={
          <span className="d-flex align-items-center justify-content-between">
            Inleveranser
            {isInternalUser && (
              <InfoIconTooltip className="ml-3">
                <h3>Nya inleveranser</h3>
                <p>
                  Här hittar du de inleveranser som kunden har laddat upp i
                  MittKontor och som ännu inte har blivit hanterade. Om du vill
                  spara filen i Dokumentarkivet, välj Flytta och välj vilken
                  mapp du vill spara filen i. När filen är sparad bland mapparna
                  flyttas filen automatiskt till Hanterade. Om du väljer att
                  använda filen på annat sätt än att spara den bland mapparna,
                  klicka på Hantera när du är klar med filen
                </p>

                <h3>Hanterade</h3>
                <p>
                  När du eller någon annan i kundteamet har klickat på Hantera
                  hamnar filen automatiskt under Hanterade. Här sparas alla
                  hanterade filer. Om du vill spara en fil i Dokumentarkivet
                  klickar du på Flytta och flyttar filen till önskad mapp. Du
                  kan inte ta bort filer från Hanterade.
                </p>
              </InfoIconTooltip>
            )}
          </span>
        }
      >
        <div className="d-flex flex-wrap">
          <div className="d-flex flex-direction-column align-items-start mr-relative">
            {!isInternalUser && (
              <LinkPrimaryArrow
                className="my-4"
                to={`/customers/${customerId}/delivery`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation({
                    url: `/customers/${customerId}/delivery`,
                    eventTitle: "Ladda upp",
                    e: e
                  });
                }}
              >
                Ladda upp
              </LinkPrimaryArrow>
            )}

            {!isInternalUser && (
              <NavLink
                className={`side-menu-link`}
                to={`/customers/${customerId}/delivery/uploaded`}
                onClick={(e) =>
                  handleNavigation({
                    url: `/customers/${customerId}/delivery/uploaded`,
                    eventTitle: "Uppladdade filer",
                    e: e
                  })
                }
              >
                Uppladdade filer
              </NavLink>
            )}

            {isInternalUser && (
              <NavLink
                className={`side-menu-link`}
                to={`/customers/${customerId}/delivery/new`}
                onClick={() => captureEvent("Nya leveranser")}
              >
                Nya leveranser
              </NavLink>
            )}

            {isInternalUser && (
              <NavLink
                className={`side-menu-link`}
                to={`/customers/${customerId}/delivery/handled`}
                onClick={() => captureEvent("Hanterade")}
              >
                Hanterade
              </NavLink>
            )}
          </div>
        </div>
      </SideMenu>

      <div className="box flex-1-1 overflow-y-auto w-100">
        <Outlet />
      </div>
    </div>
  );
}
